import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  fetchConfirmation, submitConfirmation, ConfirmLinkError, ConfirmRejectedError,
} from '../services/confirm';
import { brusselsParts } from '../utils/dates';
import './AppointmentConfirmPage.css';

/**
 * An appointment confirmed by its client through the link in their e-mail.
 * No account: the token in the URL is the access. One job — confirm, and when
 * the firm requires it, accept the general terms of sale first.
 */

const FONTS_HREF = 'https://fonts.googleapis.com/css2?family=IBM+Plex+Sans+Condensed:wght@400;500;600&family=Source+Serif+4:ital,opsz,wght@0,8..60,400;0,8..60,600;1,8..60,400&display=swap';
const FALLBACK_ACCENT = '#0f5e73';

function useConfirmHead(title) {
  useEffect(() => {
    // A personal link must never be indexed, even if it leaks.
    const robots = document.createElement('meta');
    robots.name = 'robots';
    robots.content = 'noindex, nofollow';
    document.head.appendChild(robots);
    let fonts = document.querySelector('link[data-portal-fonts]');
    if (!fonts) {
      fonts = document.createElement('link');
      fonts.rel = 'stylesheet';
      fonts.href = FONTS_HREF;
      fonts.setAttribute('data-portal-fonts', '');
      document.head.appendChild(fonts);
    }
    return () => robots.remove();
  }, []);
  useEffect(() => {
    if (title) document.title = title;
  }, [title]);
}

const isHex = (c) => /^#[0-9a-f]{6}$/i.test(c || '');
const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

// scheduled_at is a naive local ISO string (Europe/Brussels): read its parts
// as-is. Going through Date(string) or a timezone would shift the hour.
function parseNaive(value) {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/.exec(value || '');
  if (!m) return null;
  return { y: +m[1], mo: +m[2], d: +m[3], h: m[4] === undefined ? null : +m[4], mi: m[5] === undefined ? null : +m[5] };
}

function formatDay(p, lang) {
  const d = new Date(p.y, p.mo - 1, p.d);
  const text = d.toLocaleDateString(lang === 'en' ? 'en-GB' : 'fr-BE', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
  return cap(text);
}

function formatTime(p, lang) {
  if (p.h === null) return '';
  const mm = String(p.mi).padStart(2, '0');
  // "14 h 30" is the French-Belgian way of writing a time of day.
  return lang === 'en' ? `${String(p.h).padStart(2, '0')}:${mm}` : `${p.h} h${p.mi ? ` ${mm}` : ''}`;
}

// confirmed_at is naive UTC (unlike scheduled_at): convert it to Brussels time.
function formatStamp(value, lang) {
  const p = brusselsParts(value);
  if (!p) return '';
  const day = new Date(p.y, p.mo - 1, p.d).toLocaleDateString(lang === 'en' ? 'en-GB' : 'fr-BE', {
    day: 'numeric', month: 'long', year: 'numeric',
  });
  const time = formatTime(p, lang);
  return time ? `${day}, ${time}` : day;
}

function Contact({ company }) {
  const phoneHref = (company.phone || '').replace(/[^\d+]/g, '');
  return (
    <ul className="c-contact">
      {company.phone && <li><a href={`tel:${phoneHref}`}>{company.phone}</a></li>}
      {company.email && <li><a href={`mailto:${company.email}`}>{company.email}</a></li>}
    </ul>
  );
}

export default function AppointmentConfirmPage() {
  const { token } = useParams();
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'fr').startsWith('en') ? 'en' : 'fr';
  const [data, setData] = useState(null);
  const [state, setState] = useState('loading'); // loading | ready | invalid | error
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  const confirmedRef = useRef(null);

  const load = useCallback(() => {
    setState('loading');
    fetchConfirmation(token)
      .then((d) => { setData(d); setState('ready'); })
      .catch((err) => setState(err instanceof ConfirmLinkError ? 'invalid' : 'error'));
  }, [token]);

  useEffect(load, [load]);

  const status = data?.status;
  const company = data?.company || {};
  useConfirmHead(data
    ? [t('confirmPage.pageTitle'), company.name].filter(Boolean).join(' — ')
    : t('confirmPage.pageTitle'));

  // After a successful POST the button vanishes: move focus to the result so
  // keyboard and screen-reader users are not left on a removed element.
  const justConfirmed = useRef(false);
  useEffect(() => {
    if (status === 'confirmed' && justConfirmed.current && confirmedRef.current) {
      confirmedRef.current.focus();
      justConfirmed.current = false;
    }
  }, [status]);

  const accent = isHex(company.primary_color) ? company.primary_color : FALLBACK_ACCENT;
  const style = { '--p-accent': accent };

  if (state === 'loading') {
    return (
      <main className="confirm c-state" style={style} lang={lang}>
        <p className="c-state-text" role="status">{t('confirmPage.loading')}</p>
      </main>
    );
  }

  if (state === 'invalid' || state === 'error') {
    return (
      <main className="confirm c-state" style={style} lang={lang}>
        <div className="c-state-box">
          <h1 className="c-state-title">
            {state === 'invalid' ? t('confirmPage.invalidTitle') : t('confirmPage.errorTitle')}
          </h1>
          <p className="c-state-text">
            {state === 'invalid' ? t('confirmPage.invalidText') : t('confirmPage.errorText')}
          </p>
          {state === 'error' && (
            <button type="button" className="c-button" onClick={load}>{t('confirmPage.retry')}</button>
          )}
        </div>
      </main>
    );
  }

  const appt = data.appointment || {};
  const terms = data.terms || {};
  const when = parseNaive(appt.scheduled_at);
  // The arrival window told to the client (same as the booked time when no
  // window is set): « entre 13 h et 15 h ».
  const windowStart = parseNaive(appt.window_start) || when;
  const windowEnd = parseNaive(appt.window_end) || when;
  const hasWindow = !!(windowStart && windowEnd && windowStart.h !== null
    && (windowStart.h !== windowEnd.h || windowStart.mi !== windowEnd.mi));
  const needsTerms = !!(terms.available && terms.required);
  const canSubmit = !busy && (!needsTerms || accepted);

  const duration = (() => {
    const total = Number(appt.duration_minutes) || 0;
    if (!total) return '';
    const h = Math.floor(total / 60);
    const m = total % 60;
    if (!h) return t('confirmPage.durationM', { m });
    return m ? t('confirmPage.durationHM', { h, m }) : t('confirmPage.durationH', { h });
  })();

  const submit = (event) => {
    event.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setProblem('');
    submitConfirmation(token, needsTerms && accepted)
      .then((d) => { justConfirmed.current = true; setData(d); })
      .catch((err) => {
        if (err instanceof ConfirmRejectedError && err.detail === 'terms_required') {
          setProblem(t('confirmPage.errorTermsRequired'));
        } else if (err instanceof ConfirmRejectedError && err.detail === 'appointment_cancelled') {
          setProblem(t('confirmPage.errorCancelled'));
        } else if (err instanceof ConfirmLinkError) {
          setState('invalid');
        } else {
          setProblem(t('confirmPage.errorGeneric'));
        }
      })
      .finally(() => setBusy(false));
  };

  const mapHref = appt.address
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(appt.address)}`
    : '';

  const whenBlock = (
    <div className="c-when">
      <p className="c-day">{when ? formatDay(when, lang) : appt.scheduled_at}</p>
      {hasWindow ? (
        <p className="c-time">
          {t('confirmPage.between', { start: formatTime(windowStart, lang), end: formatTime(windowEnd, lang) })}
        </p>
      ) : (
        when && when.h !== null && <p className="c-time">{formatTime(when, lang)}</p>
      )}
      {duration && <p className="c-duration">{t('confirmPage.durationApprox', { duration })}</p>}
    </div>
  );

  const details = (
    <dl className="c-details">
      {appt.address && (
        <div>
          <dt>{t('confirmPage.address')}</dt>
          <dd>
            {appt.address}
            <a className="c-map" href={mapHref} target="_blank" rel="noopener noreferrer">
              {t('confirmPage.openMap')}
            </a>
          </dd>
        </div>
      )}
      {appt.title && <div><dt>{t('confirmPage.subject')}</dt><dd>{appt.title}</dd></div>}
      {appt.customer_name && <div><dt>{t('confirmPage.customer')}</dt><dd>{appt.customer_name}</dd></div>}
      {appt.requesting_company_name && (
        <div><dt>{t('confirmPage.requestedBy')}</dt><dd>{appt.requesting_company_name}</dd></div>
      )}
      {appt.client_note && (
        <div className="c-note"><dt>{t('confirmPage.note')}</dt><dd>{appt.client_note}</dd></div>
      )}
    </dl>
  );

  const contact = [company.address, company.website].filter(Boolean);

  let body;
  if (status === 'cancelled') {
    body = (
      <section className="c-card" aria-labelledby="c-title">
        <h1 id="c-title" className="c-heading">{t('confirmPage.cancelledTitle')}</h1>
        <div className="c-struck">{whenBlock}</div>
        <p className="c-note">{t('confirmPage.cancelledText')}</p>
        <Contact company={company} />
      </section>
    );
  } else if (status === 'confirmed') {
    body = (
      <section className="c-card" aria-labelledby="c-title">
        <h1 id="c-title" className="c-heading c-heading-ok" tabIndex={-1} ref={confirmedRef}>
          <svg className="c-tick" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
          {t('confirmPage.confirmedTitle')}
        </h1>
        {whenBlock}
        {details}
        <p className="c-note">
          {data.confirmed_at && <>{t('confirmPage.confirmedOn', { date: formatStamp(data.confirmed_at, lang), interpolation: { escapeValue: false } })} </>}
          {data.terms_accepted_at && terms.version && t('confirmPage.termsAccepted', { version: String(terms.version).replace(/-/g, "\u2011") })}
        </p>
        <p className="c-note">{t('confirmPage.changeText')}</p>
        <Contact company={company} />
      </section>
    );
  } else {
    body = (
      <form className="c-card" onSubmit={submit} aria-labelledby="c-title" noValidate>
        <h1 id="c-title" className="c-heading">{t('confirmPage.heading')}</h1>
        {whenBlock}
        {details}
        {terms.available && (
          <div className="c-terms">
            {!needsTerms && <p className="c-note">{t('confirmPage.termsInfo')}</p>}
            <a href={terms.url} target="_blank" rel="noopener noreferrer">{t('confirmPage.termsLink')}</a>
            {needsTerms && (
              <label className="c-check">
                <input
                  type="checkbox"
                  checked={accepted}
                  onChange={(e) => setAccepted(e.target.checked)}
                />
                <span>{t('confirmPage.termsCheckbox')}</span>
              </label>
            )}
          </div>
        )}
        {problem && <p className="c-problem" role="alert">{problem}</p>}
        {needsTerms && !accepted && <p id="c-confirm-hint" className="c-note">{t('confirmPage.confirmDisabledHint')}</p>}
        <button
          type="submit"
          className="c-button c-button-main"
          disabled={!canSubmit}
          aria-describedby={needsTerms && !accepted ? 'c-confirm-hint' : undefined}
        >
          {busy ? t('confirmPage.confirming') : t('confirmPage.confirm')}
        </button>
      </form>
    );
  }

  return (
    <div className="confirm" style={style} lang={lang}>
      <header className="c-masthead">
        <div className="c-wrap">
          {company.logo_url
            ? <img className="c-logo" src={company.logo_url} alt={company.name || ''} />
            : <span className="c-brand">{company.name}</span>}
        </div>
      </header>
      <main className="c-wrap c-main">{body}</main>
      <footer className="c-footer">
        <div className="c-wrap">
          {company.name && <p className="c-footer-name">{company.name}</p>}
          {contact.length > 0 && (
            <ul className="c-footer-list">
              {contact.map((c) => <li key={c}>{c}</li>)}
            </ul>
          )}
          {status !== 'cancelled' && status !== 'confirmed' && <Contact company={company} />}
          <p className="c-footer-note">{t('confirmPage.privateNote')}</p>
        </div>
      </footer>
    </div>
  );
}
